import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import { Alert } from '@neo/primitives/alert'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@neo/primitives/base/select'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { Flag } from '@neo/primitives/icon'
import { Separator } from '@neo/primitives/separator'
import { Tooltip } from '@neo/primitives/tooltip'
import { ReactComponent as GoogleIcon } from '@shared/assets/icons/ic-google-color.svg'
import type { SignupFormData } from '@shared/types'
import { getCountryCodeFromIP } from '@shared/utils/country-detection'
import { Info } from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Link, Navigate, useSearchParams } from 'router-v6'
import { RECAPTCHA_SITE_KEY, REGIONS } from 'v2/configs/constants'
import { getDefaultRoute } from 'v2/configs/routes'
import { DataRegionNote } from 'v2/shared/components/data-region-note'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { nameSchema } from 'v2/shared/validation-schemas'
import { AnalyticsEvents, identifyUser, setSignupCountry, track } from 'v2/utils/analytics'
import dayjs from 'v2/utils/dayjs'
import { startFingerprint } from 'v2/utils/fingerprint'
import { logger } from 'v2/utils/logger'
import { getSafeRedirectUrl } from 'v2/utils/url-validation'
import { AuthLayout } from '../../shared/layouts/auth'
import {
  AUTH_INPUT_CLASS,
  AUTH_LINK_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
} from './components/auth-styles'
import {
  useInitiateSignupMutation,
  usePreSignupCheckQuery,
  useSsoAuthorizeMutation,
} from './queries'
import { validateEmail, validatePassword } from './utils'

const regionMap = {
  [REGIONS.IN]: { label: 'India', icon: <Flag iso="IN" size={16} circle /> },
  [REGIONS.US]: { label: 'United States', icon: <Flag iso="US" size={16} circle /> },
}

enum SignupBlockCategory {
  VPN_OR_PROXY = 'vpn_or_proxy',
  REGION_BANNED = 'region_banned',
  IP_OR_NETWORK = 'ip_or_network',
  TEMPORARILY_UNAVAILABLE = 'temporarily_unavailable',
  API_ERROR = 'api_error',
  OTHER = 'other',
}

// String-matches the backend reason from /pre-signup-check into a stable
// category so PostHog breakdowns don't depend on exact message wording.
const categorizeSignupBlockReason = (
  reason: string | undefined,
  isApiError: boolean,
): SignupBlockCategory => {
  if (isApiError) return SignupBlockCategory.API_ERROR
  if (!reason) return SignupBlockCategory.OTHER
  const lower = reason.toLowerCase()
  if (lower.includes('vpn') || lower.includes('proxy')) return SignupBlockCategory.VPN_OR_PROXY
  if (lower.includes('region')) return SignupBlockCategory.REGION_BANNED
  if (lower.includes('network') || lower.includes('verify your location'))
    return SignupBlockCategory.IP_OR_NETWORK
  if (lower.includes('temporarily')) return SignupBlockCategory.TEMPORARILY_UNAVAILABLE
  return SignupBlockCategory.OTHER
}

const trafficDestinationOptions = [
  {
    value: REGIONS.IN,
    label: regionMap[REGIONS.IN].label,
    icon: regionMap[REGIONS.IN].icon,
  },
  {
    value: REGIONS.US,
    label: regionMap[REGIONS.US].label,
    icon: regionMap[REGIONS.US].icon,
  },
]

const fields: GenericFieldConfig<SignupFormData> = [
  {
    name: 'first_name',
    componentType: 'input',
    zodValidation: nameSchema('First name', 30),
    label: 'First Name',
    className: 'col-span-1',
    inputProps: {
      placeholder: 'Enter your first name',
      inputClassName: AUTH_INPUT_CLASS,
      autoComplete: 'given-name',
      'data-testid': 'input-signup-first-name',
    },
  },
  {
    name: 'last_name',
    componentType: 'input',
    zodValidation: nameSchema('Last name', 30),
    label: 'Last Name',
    className: 'col-span-1',
    inputProps: {
      placeholder: 'Enter your last name',
      inputClassName: AUTH_INPUT_CLASS,
      autoComplete: 'family-name',
      'data-testid': 'input-signup-last-name',
    },
  },
  {
    name: 'email',
    componentType: 'input',
    zodValidation: () => validateEmail(),
    label: 'Work Email',
    inputProps: {
      placeholder: 'jane@company.com',
      inputClassName: AUTH_INPUT_CLASS,
      autoComplete: 'username',
      'data-testid': 'input-signup-email',
    },
  },
  {
    name: 'password',
    componentType: 'inputPassword',
    zodValidation: (formState: SignupFormData) => {
      const emailLocalPart = formState.email?.split('@')[0] || ''

      return validatePassword(formState.first_name, formState.last_name, emailLocalPart)
    },
    label: 'Password',
    errorClassName: 'error-list-format',
    inputProps: {
      autoComplete: 'new-password',
      placeholder: 'Enter a secure password',
      inputClassName: AUTH_INPUT_CLASS,
      'data-testid': 'input-signup-password',
    },
  },
]

export const SignupV2 = () => {
  const { userAuthenticated } = useAuth()
  const recaptchaRef = useRef() as React.MutableRefObject<ReCAPTCHA>
  const [searchParams] = useSearchParams()
  const couponCode = searchParams.get('coupon_code') ?? undefined
  const showGoogleSignup = searchParams.get('isBeta') === 'true'
  const ssoError = searchParams.get('sso_error')
  // Preserve where the user was heading (e.g. the CLI consent screen) when they
  // bounce back to login, so the round trip does not lose it. getSafeRedirectUrl
  // keeps this from being turned into an off-site redirect.
  const nextUrl = getSafeRedirectUrl(searchParams.get('next_url'))
  const loginHref = nextUrl ? `/login?next_url=${encodeURIComponent(nextUrl)}` : '/login'
  const {
    mutate: initiateSignup,
    isPending: isSignupPending,
    error,
  } = useInitiateSignupMutation(recaptchaRef)
  const { mutate: ssoAuthorize, isPending: isSsoLoading } = useSsoAuthorizeMutation()
  const {
    data: preSignupCheck,
    isLoading: isPreSignupLoading,
    isError: isPreSignupError,
    isFetching: isRecheckingPreSignup,
    refetch: recheckPreSignup,
  } = usePreSignupCheckQuery(couponCode)
  const isBlocked = isPreSignupError || preSignupCheck?.data?.allowed !== true
  // Track only once per page mount — refs survive re-renders and dodge
  // React 18 StrictMode's double-effect invocation in dev.
  const hasTrackedBlockRef = useRef(false)
  useEffect(() => {
    if (hasTrackedBlockRef.current || isPreSignupLoading || !isBlocked) return
    hasTrackedBlockRef.current = true
    const reason = preSignupCheck?.data?.reason
    track(AnalyticsEvents.SIGNUP_BLOCKED, {
      reason,
      reason_category: categorizeSignupBlockReason(reason, isPreSignupError),
      has_coupon: !!couponCode,
      api_error: isPreSignupError,
    })
  }, [isPreSignupLoading, isBlocked, isPreSignupError, preSignupCheck?.data?.reason, couponCode])
  const defaultValueRef = useRef<SignupFormData>({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    region: '',
  })
  const [signupEmail, setSignupEmail] = useState<string | null>(null)
  const [selectedRegion, setSelectedRegion] = useState(REGIONS.US)
  const [hasAutoDetectedRegion, setHasAutoDetectedRegion] = useState(false)
  const [showRegionModal, setShowRegionModal] = useState(false)
  const [oauthRegion, setOauthRegion] = useState(REGIONS.US)

  // Preload the Fingerprint agent while the form is filled in; identification runs on
  // submit. Only once the form is actually submittable.
  useEffect(() => {
    if (!userAuthenticated && !isPreSignupLoading && !isBlocked) startFingerprint()
  }, [userAuthenticated, isPreSignupLoading, isBlocked])

  // Auto-detect region based on IP
  useEffect(() => {
    const detectRegion = async () => {
      if (!hasAutoDetectedRegion) {
        try {
          const countryCode = await getCountryCodeFromIP()
          setSignupCountry(countryCode)
          let detectedRegion = REGIONS.US // Default to US and rest of the world

          // If detected country is India, select India region
          if (countryCode === 'IN') {
            detectedRegion = REGIONS.IN
          }

          // Update default values
          defaultValueRef.current.region = detectedRegion
          setSelectedRegion(detectedRegion)
          setHasAutoDetectedRegion(true)
        } catch (error) {
          logger.error('Failed to auto-detect region:', error)
          // Fallback to US and rest of the world if detection fails
          defaultValueRef.current.region = REGIONS.US
          setSelectedRegion(REGIONS.US)
          setHasAutoDetectedRegion(true)
        }
      }
    }

    detectRegion()
  }, [hasAutoDetectedRegion])

  if (userAuthenticated) {
    return <Navigate to={getDefaultRoute()} />
  }

  if (isPreSignupLoading) {
    return (
      <AuthLayout pageTitle="Sign up" title="Sign up" description="">
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted-foreground/30 border-t-primary" />
        </div>
      </AuthLayout>
    )
  }

  if (!isPreSignupLoading && isBlocked) {
    return (
      <AuthLayout pageTitle="Sign up" title="Sign up unavailable" description="">
        <Alert
          variant="destructive"
          content={
            preSignupCheck?.data?.reason ||
            'Unfortunately, Plivo is not available in your region at this time'
          }
          data-testid="pre-signup-blocked-alert"
        />
        <Button
          variant="default"
          typography="action"
          className={AUTH_PRIMARY_BUTTON_CLASS}
          onClick={() => recheckPreSignup()}
          disabled={isRecheckingPreSignup}
          data-testid="pre-signup-recheck-btn"
        >
          {isRecheckingPreSignup ? 'Checking…' : 'Check again'}
        </Button>
        <div className="text-p-400 text-secondary text-center">
          Already have an account?{' '}
          <Link to={loginHref} className={AUTH_LINK_CLASS} data-testid="link-login">
            Login
          </Link>
        </div>
      </AuthLayout>
    )
  }

  if (signupEmail) {
    return (
      <AuthLayout
        pageTitle="Sign up"
        title="Activate your Account"
        description=""
        footer={
          <div className="text-p-400 text-secondary grid gap-2">
            <div>
              Already verified your email?{' '}
              <Link to={loginHref} className={AUTH_LINK_CLASS} data-testid="login">
                Login
              </Link>
            </div>
            <div>
              Didn&apos;t receive it?{' '}
              <Link
                to="/resend-activation-email"
                className={AUTH_LINK_CLASS}
                data-testid="link-signup-resend-activation"
              >
                Resend email
              </Link>
            </div>
          </div>
        }
      >
        <p className="flex flex-col">
          <span className="text-p-400 text-secondary max-w-[240px]">
            We have sent a verification email to{' '}
          </span>
          <span className="text-p-600 text-primary">{signupEmail}</span>
        </p>
        <p className="mt-2 text-p-400 text-muted-foreground">
          Click the verification link in your activation email to verify your account.
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      pageTitle="Sign up"
      title="Sign up for a 28-day free trial"
      description="No credit card required. No strings attached."
    >
      {(error || ssoError) && (
        <Alert variant="destructive" content={error ? getErrorText(error) : (ssoError ?? '')} />
      )}
      {showGoogleSignup && (
        <>
          <Button
            variant="outline"
            typography="action"
            className="flex h-12 w-full justify-start border-st2 p-0 text-left hover:border-brand"
            onClick={() => {
              setOauthRegion(selectedRegion)
              setShowRegionModal(true)
            }}
            disabled={isSsoLoading}
            disabledReason={isSsoLoading ? 'Preparing your signup…' : undefined}
            data-testid="signup-google-oauth-btn"
          >
            <span className="flex size-12 items-center justify-center">
              <GoogleIcon />
            </span>
            <Separator orientation="vertical" className="h-full" />
            <span className="ml-3">Sign up with Google</span>
          </Button>
          <Dialog open={showRegionModal} onOpenChange={setShowRegionModal}>
            <DialogContent className="max-w-[400px]">
              <DialogHeader>
                <DialogTitle>Confirm your data region</DialogTitle>
              </DialogHeader>
              <p className="text-p-400 text-secondary">
                Choose the region where your data will be stored. This affects performance,
                compliance, and billing.
              </p>
              <Select value={oauthRegion} onValueChange={(value) => setOauthRegion(value)}>
                <SelectTrigger data-testid="signup-google-region-select">
                  <div className="flex items-center gap-2">
                    {regionMap[oauthRegion]?.icon}
                    <span>{regionMap[oauthRegion]?.label}</span>
                  </div>
                </SelectTrigger>
                <SelectContent position="popper">
                  {trafficDestinationOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <span className="text-xs-600 text-primary flex items-center gap-2">
                        {option.icon} {option.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DataRegionNote region={oauthRegion} canAddOtherRegion />
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowRegionModal(false)}
                  data-testid="signup-google-region-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setShowRegionModal(false)
                    ssoAuthorize({ sso_type: 'google', flow: 'signup', region: oauthRegion })
                  }}
                  disabled={isSsoLoading}
                  data-testid="signup-google-region-confirm-btn"
                >
                  Continue with Google
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <div className="flex items-center gap-4">
            <Separator className="flex-1" />
            <span className="font-jetbrains text-label uppercase text-tertiary">OR</span>
            <Separator className="flex-1" />
          </div>
        </>
      )}
      <GenericForm
        id="signup-form"
        key={hasAutoDetectedRegion ? 'region-detected' : 'detecting-region'}
        defaultValues={defaultValueRef.current}
        fields={fields}
        onSubmit={(value) => {
          const tz = dayjs.tz.guess()
          initiateSignup(
            {
              ...value,
              region: selectedRegion,
              timezone: { tzinfo: tz, title: tz },
              coupon_code: couponCode,
            },
            {
              onSuccess: (device) => {
                // Identify by email at the very start of the funnel so the
                // pre-verification session (and web→app continuity) attaches to
                // the person, not an anonymous id. aom_uuid/org come later.
                identifyUser({
                  email: value.email,
                  name: `${value.first_name} ${value.last_name}`.trim(),
                  region: selectedRegion,
                  fingerprintVisitorId: device?.fingerprint_visitor_id,
                })
                track(AnalyticsEvents.SIGNUP_INITIATED, {
                  region: selectedRegion,
                  has_coupon: !!couponCode,
                  ...device,
                })
                setSignupEmail(value.email)
              },
            },
          )
        }}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
        isSubmitting={isSignupPending}
        submitText="Sign Up"
        includeSubmitButton={false}
      />
      <div className="flex flex-col items-center gap-1">
        <div className="flex items-center justify-center gap-1">
          <span className="text-p-400 text-secondary">Data Region </span>
          <Select value={selectedRegion} onValueChange={(value) => setSelectedRegion(value)}>
            <SelectTrigger
              className="max-w-40 border-none ring-0 shadow-none focus:ring-0"
              data-testid="select-region"
            >
              <div className="flex items-center gap-2">
                {regionMap[selectedRegion].icon}
                <span>{regionMap[selectedRegion].label}</span>
              </div>
            </SelectTrigger>
            <SelectContent position="popper">
              {trafficDestinationOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <span className="text-xs-600 text-primary flex items-center gap-2">
                    {option.icon} {option.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Tooltip content="Please select the primary region where your traffic will be sent. This helps us determine the best data region for performance, compliance, and billing.">
            <Info className="w-4 h-4 text-muted-foreground" />
          </Tooltip>
        </div>
        <DataRegionNote region={selectedRegion} canAddOtherRegion className="text-center" />
      </div>
      <FormSubmitButton
        formId="signup-form"
        typography="action"
        className={AUTH_PRIMARY_BUTTON_CLASS}
        size="xl"
        loading={isSignupPending}
        data-testid="button-signup-submit"
      >
        Create Account
      </FormSubmitButton>
      <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-0.5 text-center sm:flex-nowrap">
        <span className="text-xs-400 text-secondary">By continuing, you agree to Plivo&apos;s</span>
        <Link
          to="https://www.plivo.com/legal/tos/"
          className={`${AUTH_LINK_CLASS} text-xs-400`}
          data-testid="link-terms-of-service"
          target="_blank"
          rel="noopener"
        >
          ToS
        </Link>
        <span className="text-xs-400 text-secondary">&</span>
        <Link
          to="https://www.plivo.com/legal/privacy/"
          className={`${AUTH_LINK_CLASS} text-xs-400`}
          data-testid="link-privacy-policy"
          target="_blank"
          rel="noopener"
        >
          Privacy Policy
        </Link>
      </div>
      <div className="text-p-400 text-secondary text-center">
        Already have an account?{' '}
        <Link to={loginHref} className={AUTH_LINK_CLASS} data-testid="link-login">
          Login
        </Link>
      </div>
      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITE_KEY} />
    </AuthLayout>
  )
}
